import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Package, Image, Home, Tag, ShoppingBag, Users, Wrench, Hammer, Hash, Truck, FileText, Shield, Zap, ClipboardList, BarChart2, LogOut, Menu, X, ChevronRight, CreditCard } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';

const BASE = process.env.REACT_APP_BACKEND_URL;

const NAV = [
  { href: '/admin',             label: 'Dashboard',         icon: LayoutDashboard, exact: true },
  { href: '/admin/products',    label: 'Products',           icon: Package },
  { href: '/admin/media',       label: 'Media Library',      icon: Image },
  { href: '/admin/homepage',    label: 'Homepage',           icon: Home },
  { href: '/admin/campaigns',   label: 'Campaigns',          icon: Tag },
  { href: '/admin/dealers',     label: 'Dealers',            icon: Users, badgeKey: 'submitted' },
  { href: '/admin/payment-rules',label: 'Payment Rules',     icon: CreditCard },
  { href: '/admin/quotes',       label: 'Quotes & Pricing',  icon: FileText },
  { href: '/admin/cut-sheet',   label: 'Cut Sheet Engine',   icon: Wrench },
  { href: '/admin/orders',      label: 'Orders',             icon: ShoppingBag, badgeKey: 'new_orders' },
  { href: '/admin/shop-floor',  label: 'Shop Floor',         icon: Hammer },
  { href: '/admin/serialization',label: 'Serialization',     icon: Hash },
  { href: '/admin/shipping',    label: 'Shipping',           icon: Truck },
  { href: '/admin/compliance',  label: 'Compliance',         icon: Shield },
  { href: '/admin/inventory',   label: 'Inventory',          icon: BarChart2 },
  { href: '/admin/audit',       label: 'Audit Log',          icon: ClipboardList },
  { href: '/admin/demo',        label: 'Demo / Walkthrough', icon: Zap },
];

function SidebarLink({ item, onClick, badges }) {
  const location = useLocation();
  const isActive = item.exact
    ? location.pathname === item.href
    : location.pathname.startsWith(item.href);
  const Icon = item.icon;
  const badgeCount = item.badgeKey ? (badges?.[item.badgeKey] || 0) : 0;
  return (
    <Link
      to={item.href}
      onClick={onClick}
      data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
      className={`flex items-center gap-3 px-4 py-3 font-inter text-sm transition-all group ${
        isActive
          ? 'bg-orange-500/10 text-orange-500 border-r-2 border-orange-500'
          : 'text-zinc-400 hover:text-white hover:bg-white/5 border-r-2 border-transparent'
      }`}
    >
      <Icon size={16} className="flex-shrink-0" />
      <span className="flex-1">{item.label}</span>
      {badgeCount > 0 && (
        <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-orange-500 text-white font-inter text-[9px] font-bold rounded-full animate-pulse">
          {badgeCount}
        </span>
      )}
    </Link>
  );
}

export default function AdminLayout({ children }) {
  const { user, logout, token } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [badges, setBadges] = useState({});

  // Poll for pending dealer applications every 60s
  useEffect(() => {
    const fetchBadges = () => {
      if (!token) return;
      fetch(`${BASE}/api/admin/dealers/stats`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : {})
        .then(stats => {
          setBadges({ submitted: (stats.submitted || 0) + (stats.info_requested || 0) });
        })
        .catch(() => {});
    };
    fetchBadges();
    const interval = setInterval(fetchBadges, 60000);
    return () => clearInterval(interval);
  }, [token]);

  return (
    <div className="min-h-screen bg-[#0f0f0f] flex" data-testid="admin-layout">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-60 bg-[#111111] border-r border-white/10 flex flex-col transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:relative lg:translate-x-0`}>
        {/* Logo */}
        <div className="px-4 py-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="font-bebas text-xl text-white tracking-widest">WITT MACHINE</div>
            <div className="font-inter text-[9px] text-orange-500 tracking-widest uppercase">Admin Studio</div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-zinc-500 hover:text-white">
            <X size={18} />
          </button>
        </div>
        {/* Nav */}
        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV.map(item => (
            <SidebarLink key={item.href} item={item} onClick={() => setSidebarOpen(false)} badges={badges} />
          ))}
        </nav>
        {/* User */}
        <div className="border-t border-white/10 px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="font-inter text-xs font-medium text-white truncate">{user?.name || 'Admin'}</p>
              <p className="font-inter text-[10px] text-zinc-600 truncate">{user?.email}</p>
            </div>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="p-2 text-zinc-500 hover:text-red-400 transition-colors"
              title="Logout"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="h-14 bg-[#111111] border-b border-white/10 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden text-zinc-400 hover:text-white"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={20} />
            </button>
            <AdminBreadcrumb />
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              target="_blank"
              className="font-inter text-xs text-zinc-500 hover:text-orange-500 transition-colors hidden sm:block"
            >
              View Site →
            </Link>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto" data-testid="admin-content">
          {children}
        </main>
      </div>
    </div>
  );
}

function AdminBreadcrumb() {
  const location = useLocation();
  const parts = location.pathname.replace('/admin', '').split('/').filter(Boolean);
  return (
    <div className="flex items-center gap-1 font-inter text-xs text-zinc-500">
      <span>Admin</span>
      {parts.map((p, i) => (
        <span key={i} className="flex items-center gap-1">
          <ChevronRight size={12} />
          <span className={i === parts.length - 1 ? 'text-white capitalize' : 'capitalize'}>{p}</span>
        </span>
      ))}
    </div>
  );
}
